import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * CLAÈ Admin — Promo Codes.
 *
 * One-time-use percentage-off codes applied at checkout. Deposit is
 * never discounted (spec §3) — the saving comes off the in-clinic
 * balance only. A code becomes "Used" when the booking's deposit
 * payment clears successfully.
 */
export default function AdminPromoCodes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ code: "", discount_pct: "", max_uses: "1", active: true });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/admin/promo-codes"); setRows(r.data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load promo codes"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const code = (form.code || "").trim().toUpperCase();
    const pct = Number(form.discount_pct);
    const maxUses = parseInt(form.max_uses, 10);
    if (!code) { toast.error("Enter a promo code"); return; }
    if (!(pct > 0 && pct <= 100)) { toast.error("Discount % must be between 0 and 100"); return; }
    if (!Number.isFinite(maxUses) || maxUses < 1) { toast.error("Maximum uses must be at least 1"); return; }
    setCreating(true);
    try {
      await api.post("/admin/promo-codes", { code, discount_pct: pct, max_uses: maxUses, active: !!form.active });
      toast.success(`Promo code ${code} created`);
      setForm({ code: "", discount_pct: "", max_uses: "1", active: true });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create promo code");
    }
    setCreating(false);
  };

  const toggle = async (row) => {
    try {
      await api.patch(`/admin/promo-codes/${row.id}`, { active: !row.active });
      toast.success(`${row.code} is now ${!row.active ? "active" : "inactive"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete promo code ${row.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/promo-codes/${row.id}`);
      toast.success(`${row.code} deleted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-promo-codes">
      <div>
        <h2 className="font-serif text-2xl text-stone-900">Promo Codes</h2>
        <p className="text-xs text-stone-600 mt-1">
          One-time-use percentage discounts applied at checkout. Deposit amount is never reduced — the saving comes off the in-clinic balance only.
        </p>
      </div>

      {/* Create form */}
      <div className="border hairline bg-white p-5 space-y-3">
        <p className="mono-label">Create Promo Code</p>
        <div className="grid gap-3 md:grid-cols-[1fr_120px_140px_120px_auto]">
          <input
            data-testid="promo-code-input"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="WELCOME10"
            className="border hairline p-2 text-sm font-mono focus:outline-none focus:border-stone-900"
          />
          <input
            data-testid="promo-pct-input"
            type="number" min="1" max="100" step="0.5"
            value={form.discount_pct}
            onChange={(e) => setForm({ ...form, discount_pct: e.target.value })}
            placeholder="10 (%)"
            className="border hairline p-2 text-sm focus:outline-none focus:border-stone-900"
          />
          <input
            data-testid="promo-maxuses-input"
            type="number" min="1" step="1"
            value={form.max_uses}
            onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
            placeholder="Max uses"
            title="How many clients can use this code in total"
            className="border hairline p-2 text-sm focus:outline-none focus:border-stone-900"
          />
          <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
            <input
              data-testid="promo-active-input"
              type="checkbox"
              checked={!!form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Active
          </label>
          <button
            data-testid="promo-create-btn"
            onClick={submit}
            disabled={creating}
            className="mono-label border border-stone-900 px-4 py-2 hover:bg-stone-900 hover:text-white transition disabled:opacity-50"
          >{creating ? "Creating…" : "Create"}</button>
        </div>
        <p className="text-xs text-stone-500">Max uses = how many successful bookings can redeem this code before it becomes unavailable. Only successful payments count — applying at checkout does not.</p>
      </div>

      {/* Existing codes */}
      <div className="border hairline bg-white">
        <div className="grid grid-cols-[1fr_70px_110px_110px_1fr_auto] gap-3 px-5 py-3 border-b hairline mono-label text-xs text-stone-600">
          <span>CODE</span><span>%</span><span>STATUS</span><span>USAGE</span><span>LAST USED</span><span></span>
        </div>
        {loading && <div className="px-5 py-6 text-sm text-stone-500">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="px-5 py-6 text-sm text-stone-500">No promo codes yet.</div>
        )}
        {rows.map((r) => {
          const max = Number(r.max_uses ?? 1);
          const used = Number(r.use_count ?? (r.used ? 1 : 0));
          const exhausted = r.used || used >= max;
          return (
          <div key={r.id} data-testid={`promo-row-${r.code}`} className="grid grid-cols-[1fr_70px_110px_110px_1fr_auto] gap-3 px-5 py-3 border-b hairline items-center">
            <span className="font-mono text-sm text-stone-900">{r.code}</span>
            <span className="text-sm">{r.discount_pct}%</span>
            <span className={`mono-label text-xs ${r.active && !exhausted ? "text-emerald-700" : "text-stone-400"}`}>
              {exhausted ? "EXHAUSTED" : (r.active ? "ACTIVE" : "INACTIVE")}
            </span>
            <span data-testid={`promo-usage-${r.code}`} className={`mono-label text-xs ${exhausted ? "text-stone-900" : "text-stone-600"}`}>
              Used: {used} / Max: {max}
            </span>
            <span className="text-xs text-stone-600">
              {used > 0
                ? <>Ref <span className="font-mono">{r.used_by_booking_reference || "—"}</span> · {r.used_at ? new Date(r.used_at).toLocaleString("en-GB") : ""}</>
                : "—"}
            </span>
            <div className="flex gap-2 justify-end">
              <button
                data-testid={`promo-toggle-${r.code}`}
                onClick={() => toggle(r)}
                disabled={exhausted}
                title={exhausted ? "Maximum uses reached — cannot be reactivated" : ""}
                className="mono-label text-xs border border-stone-900 px-3 py-1 hover:bg-stone-900 hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
              >{r.active ? "Deactivate" : "Activate"}</button>
              <button
                data-testid={`promo-delete-${r.code}`}
                onClick={() => del(r)}
                disabled={used > 0}
                title={used > 0 ? "Code has been used — deactivate instead to keep the audit record" : ""}
                className="mono-label text-xs border border-red-700 text-red-700 px-3 py-1 hover:bg-red-700 hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
              >Delete</button>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}
